import { useState } from "react";
import { motion } from "framer-motion";
import { Camera, Download, Loader2, Upload, Sparkles } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fadeUp = { hidden: { opacity: 0, y: 20 }, visible: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.08, duration: 0.5 } }) };

const STYLES = [
  { id: "luxury portrait", label: "Luxury Portrait" },
  { id: "magazine cover", label: "Magazine Cover" },
  { id: "royal painting", label: "Royal Painting" },
  { id: "glamour hollywood", label: "Hollywood Glamour" },
  { id: "caribbean elegance", label: "Caribbean Elegance" },
];

export default function PhotoBoothPage() {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [style, setStyle] = useState("luxury portrait");

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) { toast.error("Max 10MB"); return; }
    setFile(f);
    setPreview(URL.createObjectURL(f));
    setResult(null);
  };

  const transform = async () => {
    if (!file) { toast.error("Upload a photo first."); return; }
    setLoading(true);
    setResult(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API}/ai/photo-booth`, formData, {
        headers: { "Content-Type": "multipart/form-data", "X-Style": style },
        timeout: 120000,
      });
      setResult(res.data.image_base64);
      toast.success("Photo transformed!");
    } catch { toast.error("Transform failed. Please try again."); } finally { setLoading(false); }
  };

  const download = () => {
    if (!result) return;
    const link = document.createElement("a");
    link.href = `data:image/png;base64,${result}`;
    link.download = `photobooth-${Date.now()}.png`;
    link.click();
  };

  return (
    <div data-testid="photo-booth-page" className="pt-20 min-h-screen bg-luxury-black">
      <section className="py-24 md:py-28">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <motion.div initial="hidden" animate="visible" className="text-center mb-12">
            <motion.p variants={fadeUp} custom={0} className="text-xs font-body font-medium uppercase tracking-[0.3em] text-gold mb-4">AI Photo Booth</motion.p>
            <motion.h1 variants={fadeUp} custom={1} className="font-heading text-4xl sm:text-5xl font-light text-white mb-4">
              Luxury <span className="gold-gradient-text">Portrait Studio</span>
            </motion.h1>
            <motion.p variants={fadeUp} custom={2} className="text-white/50 font-body text-base max-w-lg mx-auto">
              Upload your photo and our AI transforms it into a stunning luxury-styled portrait.
            </motion.p>
          </motion.div>

          {/* Style Selection */}
          <motion.div initial="hidden" animate="visible" variants={fadeUp} custom={3} className="flex flex-wrap items-center justify-center gap-3 mb-10">
            {STYLES.map(s => (
              <button key={s.id} data-testid={`booth-style-${s.id.replace(/\s+/g, "-")}`} onClick={() => setStyle(s.id)} className={`px-5 py-2.5 text-xs font-body transition-all border ${style === s.id ? "border-gold bg-gold/10 text-gold" : "border-white/10 text-white/40 hover:border-white/20"}`}>
                {s.label}
              </button>
            ))}
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Upload */}
            <motion.div initial="hidden" animate="visible" variants={fadeUp} custom={4} className="glass-card p-8">
              <h3 className="text-white font-body text-sm font-medium mb-4">Your Photo</h3>
              {!preview ? (
                <label className="flex flex-col items-center justify-center h-80 border-2 border-dashed border-white/10 hover:border-gold/30 cursor-pointer transition-colors">
                  <Upload size={32} className="text-white/20 mb-3" />
                  <p className="text-white/40 font-body text-sm">Click to upload your selfie</p>
                  <p className="text-white/20 font-body text-xs mt-1">JPG, PNG, WebP up to 10MB</p>
                  <input type="file" accept="image/*" onChange={handleFile} className="hidden" data-testid="booth-upload" />
                </label>
              ) : (
                <div className="space-y-4">
                  <img src={preview} alt="Original" className="max-h-72 mx-auto object-contain" data-testid="booth-original" />
                  <div className="flex gap-3">
                    <label className="flex-1 flex items-center justify-center gap-2 border border-white/10 text-white/50 font-body text-xs py-2 cursor-pointer hover:border-white/20 transition-colors">
                      <Camera size={14} /> Change Photo
                      <input type="file" accept="image/*" onChange={handleFile} className="hidden" />
                    </label>
                    <button onClick={transform} disabled={loading} data-testid="transform-btn" className="flex-1 flex items-center justify-center gap-2 bg-gold text-black font-body text-xs font-medium py-2 hover:bg-gold-light transition-colors disabled:opacity-50">
                      {loading ? <><Loader2 size={14} className="animate-spin" /> Transforming...</> : <><Sparkles size={14} /> Transform</>}
                    </button>
                  </div>
                </div>
              )}
            </motion.div>

            {/* Result */}
            <motion.div initial="hidden" animate="visible" variants={fadeUp} custom={5} className="glass-card p-8">
              <h3 className="text-white font-body text-sm font-medium mb-4">AI Transformation</h3>
              {loading ? (
                <div className="flex flex-col items-center justify-center h-80">
                  <Sparkles size={32} className="text-gold animate-pulse mb-4" />
                  <p className="text-white/40 font-body text-sm">AI is creating your portrait...</p>
                  <p className="text-white/20 font-body text-xs mt-2">This takes 30-60 seconds</p>
                </div>
              ) : result ? (
                <div className="space-y-4">
                  <img src={`data:image/png;base64,${result}`} alt="Transformed" className="max-h-72 mx-auto object-contain" data-testid="booth-result" />
                  <button onClick={download} data-testid="booth-download" className="w-full flex items-center justify-center gap-2 border border-gold/30 text-gold font-body text-sm py-3 hover:bg-gold/10 transition-colors">
                    <Download size={14} /> Download Portrait
                  </button>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-80">
                  <Camera size={32} className="text-white/10 mb-3" />
                  <p className="text-white/20 font-body text-sm">Your portrait will appear here</p>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
